import React from "react";
import { lazy } from "react";

// project imports
import MainLayout from "layout/MainLayout";
import Loadable from "ui-component/Loadable";
import AuthGuard from "utils/route-guard/AuthGuard";
import FinanceGuard from "utils/route-guard/FinanceGuard";
import DepositGuard from "utils/route-guard/DepositGuard";
import DepositSummaryGuard from "utils/route-guard/DepositSummaryGuard";
import WithdrawGuard from "utils/route-guard/WithdrawGuard";
import WithdrawSummaryGuard from "utils/route-guard/WithdrawSummaryGuard";
import AdminGuard from "utils/route-guard/AdminGuard";
import AdminManageGuard from "utils/route-guard/AdminManageGuard";
import WebSettingGuard from "utils/route-guard/WebSettingGuard";
import BannerSettingGuard from "utils/route-guard/BannerSettingGuard";
import PopupSettingGuard from "utils/route-guard/PopupSettingGuard";
import BankSettingGuard from "utils/route-guard/BankSettingGuard";
import PaymentSettingGuard from "utils/route-guard/PaymentSettingGuard";
import CustomerGuard from "utils/route-guard/CustomerGuard";

// member routing หน้าลูกค้า
const MemberCustomerList = Loadable(
  lazy(() => import("views/member/customer/list")),
);

const ApiSetting = Loadable(
  lazy(() => import("views/api-setting/agent-setting/agent-profile")),
);

const BankSetting = Loadable(
  lazy(() => import("views/api-setting/bank-setting/agent-profile")),
);

const PaymentSetting = Loadable(
  lazy(() => import("views/api-setting/payment-setting")),
);

const WebSetting = Loadable(lazy(() => import("views/web-setting")));

const TurnSetting = Loadable(
  lazy(() => import("views/web-setting/turnsetting")),
);

const AdminList = Loadable(lazy(() => import("views/member/admin/list")));

const AdminDetail = Loadable(
  lazy(() => import("views/member/admin/account-profile")),
);

const MemberCustomerDetail = Loadable(
  lazy(() => import("views/member/customer/account-profile")),
);

const PromotionDetail = Loadable(
  lazy(() => import("views/promotion/promotion/promotion-detail")),
);

const WhellPrize = Loadable(
  lazy(() => import("views/promotion/wheelprize/wheel-prize")),
);

const Campaign = Loadable(lazy(() => import("views/promotion/campaign")));
const CampaignDetail = Loadable(
  lazy(() => import("views/promotion/campaign/CampaignDetail")),
);
const MarketingActivity = Loadable(
  lazy(() => import("views/promotion/marketing-activity")),
);

const Cashback = Loadable(lazy(() => import("views/promotion/cashback")));

const FriendInvite = Loadable(
  lazy(() => import("views/promotion/friendinvite")),
);

const CheckinActivity = Loadable(
  lazy(() => import("views/promotion/checkin/ManagerPage")),
);

const EventList = Loadable(lazy(() => import("views/promotion/event/list")));

const EventDetail = Loadable(
  lazy(() => import("views/promotion/event/event-detail")),
);

const FreeDiamond = Loadable(lazy(() => import("views/promotion/freediamond")));

const DashboardDefault = Loadable(
  lazy(() => import("views/dashboard/Default")),
);

const DashboardAnalytics = Loadable(
  lazy(() => import("views/dashboard/Analytics")),
);

const WithdrawList = Loadable(
  lazy(() => import("views/account/withdraw/list")),
);

const DepositList = Loadable(lazy(() => import("views/account/deposit/list")));

const SummaryWithdraw = Loadable(
  lazy(() => import("views/account-summary/withdraw/list")),
);

const SummaryDeposit = Loadable(
  lazy(() => import("views/account-summary/deposit/list")),
);

const CreditCodeList = Loadable(
  lazy(() => import("views/promotion/creditcode/list")),
);

const PromotionList = Loadable(
  lazy(() => import("views/promotion/promotion/list")),
);

const NoPromotionRewardSetting = Loadable(
  lazy(() => import("views/promotion/no-promotion-reward")),
);

const Ranking = Loadable(lazy(() => import("views/promotion/ranking")));

const BannerList = Loadable(
  lazy(() => import("views/web-setting/banner/list")),
);

const BannerDetail = Loadable(
  lazy(() => import("views/web-setting/banner/banner-detail")),
);

const PopupList = Loadable(lazy(() => import("views/web-setting/popup/list")));

const PopupDetail = Loadable(
  lazy(() => import("views/web-setting/popup/popup-detail")),
);

const WordpressPluginManager = Loadable(
  lazy(() => import("views/wordpress-plugin")),
);

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: "/",
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: "/wheeling",
      element: <WhellPrize />,
    },
    {
      path: "/web-setting",
      element: (
        <WebSettingGuard>
          <WebSetting />
        </WebSettingGuard>
      ),
    },
    {
      path: "/turn-setting",
      element: <TurnSetting />,
    },
    {
      path: "/banner",
      element: (
        <BannerSettingGuard>
          <BannerList />
        </BannerSettingGuard>
      ),
    },
    {
      path: "/banner/detail/:id",
      element: (
        <BannerSettingGuard>
          <BannerDetail />
        </BannerSettingGuard>
      ),
    },
    {
      path: "/popup",
      element: (
        <PopupSettingGuard>
          <PopupList />
        </PopupSettingGuard>
      ),
    },
    {
      path: "/popup/detail/:id",
      element: (
        <PopupSettingGuard>
          <PopupDetail />
        </PopupSettingGuard>
      ),
    },
    {
      path: "/wordpress-plugin",
      element: <WordpressPluginManager />,
    },
    {
      path: "/banksetting",
      element: (
        <BankSettingGuard>
          <BankSetting />
        </BankSettingGuard>
      ),
    },
    {
      path: "/paymentsetting",
      element: (
        <PaymentSettingGuard>
          <PaymentSetting />
        </PaymentSettingGuard>
      ),
    },
    {
      path: "/agentinfo",
      element: <ApiSetting />,
    },
    {
      path: "/member/list",
      element: (
        <CustomerGuard>
          <MemberCustomerList />
        </CustomerGuard>
      ),
    },
    {
      path: "/admin/list",
      element: (
        <AdminGuard>
          <AdminList />
        </AdminGuard>
      ),
    },
    {
      path: "/admin/profile/:id",
      element: (
        <AdminManageGuard>
          <AdminDetail />
        </AdminManageGuard>
      ),
    },
    {
      path: "/member/profile/:id",
      element: (
        <CustomerGuard>
          <MemberCustomerDetail />
        </CustomerGuard>
      ),
    },
    {
      path: "/promotion/detail/:id",
      element: <PromotionDetail />,
    },
    {
      path: "/account/withdraw",
      element: (
        <WithdrawGuard>
          <WithdrawList />
        </WithdrawGuard>
      ),
    },
    {
      path: "/account/deposit",
      element: (
        <DepositGuard>
          <DepositList />
        </DepositGuard>
      ),
    },
    {
      path: "/account-summary/withdraw",
      element: (
        <WithdrawSummaryGuard>
          <SummaryWithdraw />
        </WithdrawSummaryGuard>
      ),
    },
    {
      path: "/account-summary/deposit",
      element: (
        <DepositSummaryGuard>
          <SummaryDeposit />
        </DepositSummaryGuard>
      ),
    },

    {
      path: "/creditcode",
      element: <CreditCodeList />,
    },

    {
      path: "/promotion",
      element: <PromotionList />,
    },
    {
      path: "/promotion/no-promotion-reward",
      element: <NoPromotionRewardSetting />,
    },
    {
      path: "/dashboard/default",
      element: <DashboardDefault />,
    },
    {
      path: "/dashboard/analytics",
      element: (
        <FinanceGuard>
          <DashboardAnalytics />
        </FinanceGuard>
      ),
    },
    {
      path: "/Campaign",
      element: <Campaign />,
    },
    {
      path: "/campaign/detail/:id",
      element: <CampaignDetail />,
    },
    {
      path: "/ranking",
      element: <Ranking />,
    },
    {
      path: "/friend-invite",
      element: <FriendInvite />,
    },
    {
      path: "/cashback",
      element: <Cashback />,
    },
    {
      path: "/checkin",
      element: <CheckinActivity />,
    },
    {
      path: "/event",
      element: <EventList />,
    },
    {
      path: "/marketing-activity",
      element: <MarketingActivity />,
    },
    {
      path: "/event/detail/:id",
      element: <EventDetail />,
    },
    {
      path: "/freediamond",
      element: <FreeDiamond />,
    },
  ],
};

export default MainRoutes;
